import React, { useState, useEffect, lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { HelmetProvider, Helmet } from 'react-helmet-async';
import { useAuth } from './hooks/useAuth';
import Navbar from './components/Navbar';
import Hero from './components/Hero';
import Marquee from './components/Marquee';
import Stats from './components/Stats';
import HowItWorks from './components/HowItWorks';
import Services from './components/Services';
import Fleet from './components/Fleet';
import WhyChooseUs from './components/WhyChooseUs';
import RouteCoverage from './components/RouteCoverage';
import BookingForm from './components/BookingForm';
import Testimonials from './components/Testimonials';
import FAQ from './components/FAQ';
import Footer from './components/Footer';
import WAButton from './components/WAButton';
import WAPopup from './components/WAPopup';
import StickyBar from './components/StickyBar';
import Loader from './components/Loader';
import ScrollToTopOnRoute from './components/ScrollToTopOnRoute';
import PrivacyPolicy from './components/PrivacyPolicy';
import Terms from './components/Terms';
import Compliance from './components/Compliance';
import NotFound from './components/NotFound';
import AboutUs from './components/AboutUs';

// Lazy-loaded portal pages
const Login = lazy(() => import('./components/Login'));
const DriverDashboard = lazy(() => import('./components/DriverDashboard'));
const AdminDashboard = lazy(() => import('./components/AdminDashboard'));
const TrackShipment = lazy(() => import('./components/TrackShipment'));

const PortalLoader = () => (
  <div className="min-h-screen bg-brand-bg flex items-center justify-center">
    <div className="w-8 h-8 border-2 border-brand-orange/30 border-t-brand-orange rounded-full animate-spin" />
  </div>
);

// Auth guard — redirects to /login if not authenticated
const ProtectedRoute = ({ children, adminOnly = false, authInfo }) => {
  const { user, loading, isAdmin } = authInfo;

  if (loading) return <PortalLoader />;
  if (!user) return <Navigate to="/login" replace />;
  if (adminOnly && !isAdmin) return <Navigate to="/login" replace />;
  return children;
};

// Redirect already-logged-in users away from /login
const PublicRoute = ({ children, authInfo }) => {
  const { user, loading, isAdmin, isDriver } = authInfo;
  if (loading) return <PortalLoader />;
  if (user) {
    if (isAdmin) return <Navigate to="/admin/dashboard" replace />;
    if (isDriver) return <Navigate to="/driver/dashboard" replace />;
  }
  return children;
};

const Home = () => (
  <>
    <Helmet>
      <title>PKRouteX | Premium Truck Logistics &amp; Transport Services India</title>
      <meta name="description" content="PKRouteX offers premium truck logistics, GPS-tracked fleet transport, and real-time shipment tracking across India. Book Dost Plus, Bada Dost, Eicher trucks from 10FT to 32FT instantly. Serving 250+ cities pan-India." />
      <meta name="keywords" content="logistics company India, truck transport, full truck load, part load, Eicher truck, Dost Plus, GPS tracking, pan India logistics, freight services, cargo transport, PKRouteX, same day dispatch, industrial logistics" />
      <meta property="og:title" content="PKRouteX | Premium Truck Logistics &amp; Transport Services India" />
      <meta property="og:description" content="GPS-tracked fleet, 250+ cities, 50,000+ deliveries. Book trucks from 10ft to 32ft instantly." />
      <meta property="og:type" content="website" />
      <meta property="og:image" content="https://pkroutex.com/images/fleet.png" />
      <meta name="twitter:card" content="summary_large_image" />
    </Helmet>
    <Hero />
    <Marquee />
    <Stats />
    <HowItWorks />
    <Services />
    <Fleet />
    <WhyChooseUs />
    <RouteCoverage />
    <BookingForm />
    <Testimonials />
    <FAQ />
  </>
);

const PORTAL_ROUTES = ['/login', '/driver', '/driver/dashboard', '/x-admin', '/admin', '/admin/dashboard', '/admin/drivers', '/admin/trucks', '/admin/trips'];

function AppInner() {
  const [loading, setLoading] = useState(true);
  const location = useLocation();
  const authInfo = useAuth();
  const isPortal = PORTAL_ROUTES.includes(location.pathname);

  useEffect(() => {
    const timer = setTimeout(() => setLoading(false), 2000);
    return () => clearTimeout(timer);
  }, []);

  return (
    <div className="bg-brand-bg min-h-screen text-brand-black overflow-x-hidden">
      <AnimatePresence>
        {loading && !isPortal && <Loader />}
      </AnimatePresence>

      {(!loading || isPortal) && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6 }}
        >
          <Suspense fallback={<PortalLoader />}>
            <Routes>
              <Route path="/" element={<><Navbar /><Home /><Footer /><WAButton /><WAPopup /><StickyBar /></>} />
              <Route path="/about" element={<><Navbar /><AboutUs /><Footer /></>} />
              <Route path="/privacy" element={<><Navbar /><PrivacyPolicy /><Footer /></>} />
              <Route path="/terms" element={<><Navbar /><Terms /><Footer /></>} />
              <Route path="/compliance" element={<><Navbar /><Compliance /><Footer /></>} />
              <Route path="/track" element={<><Navbar /><TrackShipment /><Footer /></>} />
              <Route path="/tracking" element={<><Navbar /><TrackShipment /><Footer /></>} />
              <Route path="/login" element={<PublicRoute authInfo={authInfo}><Login /></PublicRoute>} />
              <Route path="/driver" element={<Navigate to="/driver/dashboard" replace />} />
              <Route path="/driver/dashboard" element={<ProtectedRoute authInfo={authInfo}><DriverDashboard /></ProtectedRoute>} />
              <Route path="/x-admin" element={<Navigate to="/admin/dashboard" replace />} />
              <Route path="/admin" element={<Navigate to="/admin/dashboard" replace />} />
              <Route path="/admin/dashboard" element={<ProtectedRoute adminOnly authInfo={authInfo}><AdminDashboard initialTab="dashboard" /></ProtectedRoute>} />
              <Route path="/admin/drivers" element={<ProtectedRoute adminOnly authInfo={authInfo}><AdminDashboard initialTab="drivers" /></ProtectedRoute>} />
              <Route path="/admin/trucks" element={<ProtectedRoute adminOnly authInfo={authInfo}><AdminDashboard initialTab="trucks" /></ProtectedRoute>} />
              <Route path="/admin/trips" element={<ProtectedRoute adminOnly authInfo={authInfo}><AdminDashboard initialTab="trips" /></ProtectedRoute>} />
              <Route path="*" element={<><Navbar /><NotFound /><Footer /></>} />
            </Routes>
          </Suspense>
        </motion.div>
      )}
    </div>
  );
}

function App() {
  return (
    <HelmetProvider>
      <Router>
        <ScrollToTopOnRoute />
        <AppInner />
      </Router>
    </HelmetProvider>
  );
}

export default App;
